import { Component, ElementRef, HostListener, Input, OnChanges, SimpleChanges, ViewChild } from '@angular/core';
import * as echarts from 'echarts';
import { IBarChart, IChartData } from './bar-chart.model';
import { ChartsUtilsService } from '../../services/charts-utils.service';

@Component({
    selector: 'app-apache-bar-chart',
    templateUrl: './apache-bar-chart.component.html',
    styleUrls: ['./apache-bar-chart.component.scss'],
})
export class ApacheBarChartComponent implements OnChanges {
    @Input() barChartData: IChartData;
    @Input() isDataInPercentage = false;
    @Input() isAllowedToStackInSmallDevice = true;
    @Input() isSmallDevice = false;
    @Input() showLegend = true;
    @Input() displayDecimalValues = false;
    @ViewChild('chart', { static: true }) chartElementRef: ElementRef;

    stackSortOrder: string[];
    selectedElements: { [name: string]: boolean } = null;
    isDataLabel: boolean = false;
    isAllDataLabel: boolean = false;
    removeZerosTooltip: boolean = false;
    totalLabel: string = 'Total';
    productNames: string[] = [];
    dataTypes: { name: string; stack?: string }[] = [];
    xAxisData: string[] = [];
    // variable is created to adjust height on the webpage and display image which tells user that there is no data to display
    isNoData = true;
    _data: IBarChart[] = [];
    chart: any = undefined;
    // variables are created for displaying yAxis with some default value when there is no data to display
    hasProducts: boolean = false;
    barsColors = [];
    showInSeparateColumns = false;
    isHourly = false;
    private dataSeries: any = [];
    private isStackLabel: boolean = false;
    private onLegendSelectChangedEvent: any;
    private sortAlphabetically: boolean;

    @HostListener('window:resize', ['$event'])
    onWindowResize() {
        if (this.chart && this._data) {
            this.chart.resize();
        }
    }

    constructor(private chartUtils: ChartsUtilsService) {}

    ngOnChanges(changes: SimpleChanges): void {
        if (changes.isSmallDevice) {
            this.isSmallDevice = changes.isSmallDevice.currentValue;
        }
        if (changes.barChartData) {
            this.loadData(changes.barChartData.currentValue);
        }
    }

    loadData(data: IChartData) {
        this.barsColors = data.barsColors;
        this.isHourly = data.isDisplayingHourly;
        this.showInSeparateColumns = data.showInSeparateColumns;
        this.isDataLabel = data.isDataLabel;
        this.isStackLabel = data.isStackLabel;
        this.isAllDataLabel = data.isAllDataLabel;
        this.removeZerosTooltip = data.removeZerosTooltip;
        this.totalLabel = data.totalLabel !== undefined ? data.totalLabel : 'Total';
        this.sortAlphabetically = data.sortAlphabetically;
        this.stackSortOrder = data.stackSortOrder;
        const chartData = [...data.chartData];
        this._data = [...chartData];
        this.productNames = this._getAllProductsNames(this._data);
        if (data.sortAlphabetically) {
            this.productNames = this.productNames.sort();
        }
        this.dataTypes = this._getAllDataTypes(this._data, this.productNames);
        if (data.sortAlphabetically) {
            this.dataTypes = this.dataTypes.sort((a, b) => {
                if (this.stackSortOrder && this.stackSortOrder.length > 0) {
                    const stackA = this.stackSortOrder.indexOf(a.stack);
                    const stackB = this.stackSortOrder.indexOf(b.stack);
                    return stackA - stackB || a.name.localeCompare(b.name);
                } else {
                    const stackA = a.stack || '';
                    const stackB = b.stack || '';
                    return stackA.localeCompare(stackB) || a.name.localeCompare(b.name);
                }
            });
            data.chartData = data.chartData.map((d) => {
                return {
                    ...d,
                    products: d.products.sort((a, b) => {
                        if (this.stackSortOrder && this.stackSortOrder.length > 0) {
                            const stackA = this.stackSortOrder.indexOf(a.stack);
                            const stackB = this.stackSortOrder.indexOf(b.stack);
                            return stackA - stackB || a.label.localeCompare(b.label);
                        } else {
                            const stackA = a.stack || '';
                            const stackB = b.stack || '';
                            return stackA.localeCompare(stackB) || a.label.localeCompare(b.label);
                        }
                    }),
                };
            });
        }
        this.initializeChart();
        this.hasProducts = this._checkIfDataHasProductElements(chartData);
        if (this._data.length && this.chart && !data.isForcedNoDataState) {
            this.isNoData = false;
            this._renderChart(this._data);
        } else {
            this.isNoData = true;
            this._clearChart();
            this._resizeChart();
        }
    }

    private initializeChart(): void {
        if (!this.chart) {
            this.chart = echarts.init(this.chartElementRef.nativeElement, null, { renderer: 'canvas' });
            this.onLegendSelectChangedEvent = this.chart.on('legendselectchanged', (params) => {
                this.selectedElements = params.selected;
                this._renderChart(this._data);
            });
        }
    }

    private _clearChart(): void {
        this.chart.off('legendselectchanged', this.onLegendSelectChangedEvent);
        this.chart.clear();
    }

    private findTopValues(data: IBarChart[]) {
        const topIndices: { [index: number]: number[] } = {};
        for (let i = 0; i < data.length; i++) {
            topIndices[i] = [];
            const topIndicesInDateMap: { [stack: string]: number } = {};
            for (let j = 0; j < data[i].products.length; j++) {
                if (this.selectedElements && !this.selectedElements[data[i].products[j].label]) {
                    continue;
                }
                if (data[i].products[j].stack !== undefined) {
                    if (Number(data[i].products[j].amount) !== 0 || topIndicesInDateMap[data[i].products[j].stack] === undefined) {
                        topIndicesInDateMap[data[i].products[j].stack] = j;
                    }
                }
            }
            topIndices[i] = Object.values(topIndicesInDateMap);
        }

        return Object.values(topIndices);
    }

    private _renderChart(data: IBarChart[]): void {
        this.dataSeries = this._generateSeries(data);
        if (this.isDataLabel && this.dataSeries) {
            this.addDataLabel();
        }

        const isNoAmountsForChart = this._checkIsNoAmountsForChart(data);

        if (data) {
            this.xAxisData = this._generateAxisData(data);

            const option = {
                legend: this.showLegend && {
                    data: this.productNames,
                    orient: 'horizontal',
                    bottom: 0,
                    left: 'center',
                    right: 'auto',
                    icon: 'circle',
                    itemGap: 20,
                    textStyle: {
                        fontSize: 15,
                    },
                    type: 'scroll',
                },
                tooltip: {
                    trigger: 'axis',
                    axisPointer: {
                        type: 'shadow',
                    },
                    formatter: (params) => {
                        let tooltip = params[0].name + '<br/>';

                        let tooltipData: any[];
                        if (this.sortAlphabetically) {
                            tooltipData = params.sort((a, b) => {
                                let stackA: any;
                                let stackB: any;
                                if (this.stackSortOrder && this.stackSortOrder.length > 0) {
                                    stackA = this.stackSortOrder.indexOf(a.data.stack);
                                    stackB = this.stackSortOrder.indexOf(b.data.stack);
                                } else {
                                    stackA = a.data.stack || '';
                                    stackB = b.data.stack || '';
                                }

                                const aSeriesName = stackA ? `${a.seriesName} (${stackA})` : a.seriesName;
                                const bSeriesName = stackB ? `${b.seriesName} (${stackB})` : b.seriesName;
                                return aSeriesName.localeCompare(bSeriesName);
                            });
                        } else {
                            tooltipData = params;
                        }
                        tooltipData.forEach((item) => {
                            const seriesName = item.data.stack
                                ? `${item.seriesName} (${item.data.stack})`
                                : item.seriesName;

                            if (this.removeZerosTooltip) {
                                if (item.value !== 0) {
                                    tooltip += item.marker + seriesName + ': ' + Number(item.value) + '<br/>';
                                }
                            } else {
                                tooltip += item.marker + seriesName + ': ' + Number(item.value) + '<br/>';
                            }
                        });

                        let totalValue = 0;
                        params.forEach((item) => {
                            totalValue += Number(item.value);
                        });
                        tooltip += `<strong>${this.totalLabel}: ${this.chartUtils.formatNumber(totalValue)}</strong>`;

                        return tooltip;
                    },
                },
                xAxis: {
                    data: this.xAxisData,
                    name: this.isSmallDevice ? undefined : 'Date',
                    axisLabel: {
                        interval: this.isSmallDevice ? 1 : 0,
                        fontSize: 12,
                        rotate: this.isSmallDevice ? 60 : 0,
                    },
                    axisLine: { onZero: true },
                    splitLine: { show: false },
                    splitArea: { show: false },
                },
                yAxis: {
                    type: 'value',
                    min: !this.hasProducts ? 0 : undefined,
                    max: !this.hasProducts ? 1 : undefined,
                    axisLabel: {
                        formatter: function (value) {
                            if (this.isDataInPercentage) {
                                return isNoAmountsForChart ? value * 100 + '%' : value + '%';
                            } else {
                                if (this.displayDecimalValues) {
                                    return value;
                                } else {
                                    return Number.isInteger(value) ? value : undefined;
                                }
                            }
                        }.bind(this),
                        fontSize: 12,
                    },
                },
                grid: {
                    left: this.isSmallDevice ? 50 : 80,
                    right: this.isSmallDevice ? 0 : 80,
                    top: 30,
                    bottom: 100,
                },
                series: this.dataSeries,
            };

            // Creating tooltip for data display in percentage
            if (this.isDataInPercentage) {
                option.tooltip = {
                    ...option.tooltip,
                    formatter: (params) => {
                        let tooltip = params[0].name + '<br/>';
                        for (let i = 0; i < params.length; i++) {
                            let value = params[i].value;
                            if (typeof value === 'string') {
                                value = parseFloat(value);
                            }
                            if (!isNaN(value)) {
                                value += '%';
                            }
                            tooltip += params[i].marker + params[i].seriesName + ': ' + value + '<br/>';
                        }
                        return tooltip;
                    },
                } as any;
            }

            // Assign specific colors
            if (this.barsColors && this.barsColors.length) {
                const colorsNamesMap: { [name: string]: string } = {};
                for (let i = 0; i < this.productNames.length; i++) {
                    colorsNamesMap[this.productNames[i]] = this.barsColors[i];
                }
                option.series.forEach((el) => {
                    el.color = colorsNamesMap[el.name];
                });
            }

            this.chart.setOption(option);
            this._resizeChart();
        } else {
            this.chart.clear();
            this._resizeChart();
        }

        // removes cursor: pointer from chart series
        this.chart.on('mousemove', () => {
            this.chart.getZr().setCursorStyle('default');
        });
    }

    private _resizeChart(): void {
        setTimeout(() => {
            this.chart.resize();
        }, 0);
    }

    private _generateSeries(data: IBarChart[]): any {
        return this.dataTypes.map((product) => {
            return {
                name: product.name,
                type: 'bar',
                stack:
                    this.isAllowedToStackInSmallDevice &&
                    (this.isSmallDevice || (!this.showInSeparateColumns && !product.stack) ? 'one' : product.stack),
                barWidth: this.isSmallDevice ? 20 : this.showInSeparateColumns ? 16 : 20,
                data: this._mapProductData(product.name, data, product.stack),
                barGap: this.isSmallDevice ? undefined : this.showInSeparateColumns ? 0 : undefined,
                label: {
                    show: this.isAllDataLabel,
                    position: 'top',
                    formatter: (params) => {
                        return params.data.value === 0 ? '' : Number(params.data.value);
                    },
                },
                itemStyle: {
                    normal: {
                        cursor: 'default',
                    },
                },
            };
        });
    }

    private addDataLabel(): void {
        const sums = [];
        this.dataSeries.forEach((series) => {
            series.data.forEach((item, index) => {
                if (!sums[index]) {
                    sums[index] = 0;
                }
                if (!this.selectedElements || this.selectedElements[series.name]) {
                    sums[index] = Number(sums[index]) + Number(item.value);
                }
            });
        });

        if (this.dataSeries.length === 0) {
            return;
        }

        const lastIndex = this.dataSeries.reduce(
            (last: number, series: any, i: number) =>
                !this.selectedElements || this.selectedElements[series.name] ? i : last,
            0
        );

        this.dataSeries = this.dataSeries.map((el: any, index: number) => {
            if (lastIndex === index) {
                el.label = {
                    show: true,
                    position: 'top',
                    formatter: (params: any) => {
                        return sums[params.dataIndex] === 0 ? '' : Math.round(sums[params.dataIndex] * 100) / 100;
                    },
                };
            }
            return el;
        });
    }

    private _generateAxisData(data): string[] {
        if (this.isHourly) {
            // debugger;
            return data.reduce((acc, obj) => {
                acc.push(this.convertToPmAm(Number(obj.date.split(' ')[1].substring(0, 2))));
                return acc;
            }, []);
        } else {
            return data.reduce((acc, obj) => {
                acc.push(obj.date);
                return acc;
            }, []);
        }
    }

    private _getAllDataTypes(data: IBarChart[], productNames: string[]): { name: string; stack?: string }[] {
        const labelSetStackMap: { [label: string]: Set<string> } = {};
        for (const productName of productNames) {
            labelSetStackMap[productName] = new Set();
        }

        for (const entry of data) {
            for (const product of entry.products) {
                if (labelSetStackMap[product.label]) {
                    labelSetStackMap[product.label].add(product.stack);
                }
            }
        }

        const dataTypes: { name: string; stack?: string }[] = [];

        for (const [label, stacksSet] of Object.entries(labelSetStackMap)) {
            const stacks = Array.from(stacksSet);
            if (stacks.length === 0) {
                dataTypes.push({ name: label });
                continue;
            }
            for (const stack of stacks) {
                dataTypes.push({ name: label, stack: stack });
            }
        }

        return dataTypes;
    }

    private _getAllProductsNames(data: IBarChart[]): string[] {
        const labelSet = new Set();

        if (data) {
            for (const entry of data) {
                const { products: innerData } = entry;
                for (const item of innerData) {
                    const { label } = item;
                    labelSet.add(label);
                }
            }
            return Array.from(labelSet) as string[];
        }
        return [];
    }

    private _mapProductData(
        productName: string,
        data: IBarChart[],
        stack: string
    ): { value: number; stack?: string }[] {
        const topValues = this.findTopValues(data);
        return data.map((el, index) => {
            const productIndex = el.products.findIndex(
                (element) => element.label === productName && element.stack === stack
            );
            const product = el.products[productIndex];
            const d: any = {
                value: product ? product.amount : 0,
                stack: product ? product.stack : undefined,
            };
            if (this.isStackLabel) {
                d.label = {
                    show: topValues[index].includes(productIndex) && product && product.stack && Number(d.value) !== 0,
                    position: 'top',
                    formatter: product && product.stack,
                    rotate: 45,
                };
            }
            return d;
        });
    }

    private _checkIsNoAmountsForChart(data: IBarChart[]) {
        for (const el of data) {
            for (const product of el.products) {
                if (product.amount !== 0) {
                    return false;
                }
            }
        }
        return true;
    }

    private _checkIfDataHasProductElements(data: IBarChart[]) {
        for (const el of data) {
            if (el.products.length) {
                return true;
            }
        }
        return false;
    }

    private convertToPmAm(number: number): string {
        if (number === 24) {
            return '12am';
        } else if (number < 12) {
            return `${number}am`;
        } else if (number === 12) {
            return '12pm';
        } else {
            return `${number - 12}pm`;
        }
    }
}
